import { useMemo } from 'react'

import { useOrgCodeOrThrow } from '../../../active-operation'
import { useApiClient, useMutation, useQueryClient } from '../../../api-provider'
import { setMapArrival } from '../arrival'
import type { RequestedMapAction } from './actions'
import type { MapCatalog } from './catalog'
import { createMapCatalog } from './createMapCatalog'
import { type ResolutionOutcome, runResolution } from './runResolution'

/** The catalog over live data, bound to the active operation. */
export const useMapCatalog = (): MapCatalog => {
  const apiClient = useApiClient()
  const queryClient = useQueryClient()
  const orgCode = useOrgCodeOrThrow()

  return useMemo(
    () => createMapCatalog({ queryClient, apiClient, orgCode }),
    [queryClient, apiClient, orgCode]
  )
}

export const useMapAction = ({
  onSuccess
}: {
  onSuccess?: (outcome: ResolutionOutcome, action: RequestedMapAction) => void
} = {}) => {
  const catalog = useMapCatalog()

  const mutation = useMutation({
    onSuccess,
    mutationFn: async (action: RequestedMapAction) => {
      const outcome = await runResolution(action, catalog)
      // Any move that lands supersedes a failed arrival: whatever the last link
      // could not find, the map is no longer sitting on it.
      if (outcome.kind === 'Landed') setMapArrival({ status: 'arrived' })
      return outcome
    }
  })

  return {
    ...mutation,
    /**
     * Reported here rather than on `MapState` because it describes an attempt
     * rather than the map, and so belongs to neither the URL nor the session.
     */
    failure: mutation.data?.kind === 'Failed' ? mutation.data.failure : null
  }
}
